<template>
  <main style="--theme:#dadada">
    <!-- Hero section -->
    <section class="relative flex items-end md:items-center" style="height: 90dvh">
      <base-video v-bind="hero.video" class="absolute-0" />
      <div v-style:p="{ sm: '0 20vw 8vw 8vw', md: '0 0 0 8vw' }" class="relative" style="max-width:25rem">
        <base-picture v-style:w="{ md: '17.5rem' }" v-bind="hero.logo" class="mb-6" />
        <p class="mb-6 c-white lh-3">
          {{ hero.paragraph }}
        </p>
        <div class="w-fit f-col space-y-3">
          <template v-for="({ title, to }, key) in hero.buttons" :key>
            <vf-button
              v-if="key !== hero.buttons.length - 1"
              class="ws-nowrap"
              size="tiny"
              :to
              variant="primary-inverse"
            >
              <template v-if="!key">
                {{ $t.shopMens }}
              </template>
              <template v-if="key === 1">
                {{ $t.shopWomens }}
              </template>
            </vf-button>
            <vf-button
              v-if="key === hero.buttons.length - 1"
              size="tiny"
              :to
              variant="primary-inverse"
              @click="scrollToElement('productGrid', { offset: -35 })"
            >
              {{ title }}
            </vf-button>
          </template>
        </div>
      </div>
      <custom-arrow-scroll
        class="absolute bottom-0 mb-3 c-white <md:right-0 md:left-0"
        hide-label
        size="sm"
        style="margin-inline: 3vw"
        to="#innovation"
      />
      <div
        class="animation-bg-scroll-y absolute right-0 mr-12 h-full <md:hidden"
        :style="{
          aspectRatio: '1 / 19',
          background: `url(${hero.bgStripeSrc}) repeat-y center / auto 50%`,
        }"
      />
    </section>
    <!-- Innovation section -->
    <section
      id="innovation"
      class="relative min-h-screen c-black"
      style="background-color: #dadada"
      :style="`height: ${pxToRem(parallaxHeight)}`"
    >
      <div ref="innovationRef" class="sticky overflow-hidden <md:z-1 md:h-screen" style="top:4rem">
        <div class="center-absolute <md:hidden" style="width: 105%">
          <base-picture v-bind="innovation.bgIcon" class="animation-bounce-x op-20 invert" />
        </div>
        <div
          v-style="{
            'max-w': { sm: '85vw', md: '20vw' },
            'ml': '8vw',
            'bgv': { sm: 'linear-gradient(to bottom, #dadada 0%, #dadada 85%, rgba(218, 218, 218, 0) 100%)',
                     md: 'transparent' },
          }"
          class="relative left-0 top-0 f-col md:absolute md:top-1/2 md:-translate-y-1/2 <md:py-16"
        >
          <span
            v-style="{
              w: { sm: '14vw', md: '2.8vw' },
              h: { sm: '1.6vw', md: '.3vw' },
              my: { sm: '8vw', md: '1.6vw' },
            }"
            class="bg-black"
          />
          <h2
            v-style:text-size="{ sm: '11vw', md: 'clamp(1.25rem, 2.5vw, 3rem)' }"
            v-html="innovation.title"
            class="fw-medium lh-none"
          />
        </div>
      </div>
      <div
        ref="parallaxRef"
        v-style="{
          w: { sm: '85vw', md: '51vw' },
          ml: { sm: '8vw' },
          mr: { md: '8vw' },
        }"
        class="absolute md:right-0 md:top-1/2 md:-translate-y-1/2 <md:z-0 <md:pb-16"
      >
        <p
          v-style:text-size="{ sm: '8.5vw', md: '3.75vw' }"
          v-html="innovation.copy"
          class="fw-light lh-tight"
        />
      </div>
    </section>
    <!-- Dots scrollable shoe characteristics -->
    <section
      v-style="{ 'p': { md: '0 0 0 8vw', sm: '0 0 8vw 0' }, 'min-h': { md: '60rem' } }"
      class="md:flex md:c-white"
      :style="{ background: `url(${scrollableChars.bgImage}) fixed center/cover` }"
    >
      <!-- Left side -->
      <div
        v-style="{ 'p': { sm: '8vw 16vw 0 8vw', md: '0' }, 'min-h': { md: '60rem' } }"
        class="md:sticky md:top-0 md:h-screen md:w-1/5 md:f-col md:justify-center"
      >
        <span
          v-style="{
            w: { sm: '14vw', md: '2.8vw' },
            h: { sm: '1.6vw', md: '.3vw' },
            my: { sm: '8vw', md: '1.6vw' },
          }"
          class="bg-white"
        />
        <h2
          v-style:text-size="{ sm: '9vw', md: '3vw' }"
          class="mb-6 fw-medium lh-none"
        >
          {{ scrollableChars.title }}
        </h2>
        <p
          v-style:text-size="{ sm: '5vw', md: '1.25vw' }"
          v-html="scrollableChars.paragraph"
          class="mb-18 lh-tight"
        />
        <base-picture v-bind="scrollableChars.sections[scrollCharsIndex].detailImage" class="w-5/6 <md:hidden" />
      </div>
      <!-- Carousel (SM only) -->
      <div class="c-white md:hidden" style="padding-inline: 8vw">
        <base-scroll class="text-center space-y-6">
          <div v-for="({ title, paragraph, dotTitle, animatedImage }, key) in scrollableChars.sections" :key class="w-full">
            <base-picture v-bind="animatedImage" />
            <p v-html="dotTitle" class="lh-tight uppercase" style="font-size: 4vw" />
            <h2 v-html="title" class="fw-medium lh-none" style="font-size: 8vw" />
            <p v-html="paragraph" class="lh-tight" style="font-size: 4vw" />
          </div>
          <template #pagination="{ activeItem, pages, action }">
            <div class="mt-6 text-center space-x-10">
              <base-button
                v-for="(page, key) in pages"
                :key
                class="relative aspect-square w-6 rounded-full bg-white duration"
                :class="{ 'scale-50': activeItem === key }"
                @click="action(key)"
              >
                <div class="animation-pulsate absolute left-1/2 full rounded-full bg-white" />
              </base-button>
            </div>
          </template>
        </base-scroll>
      </div>
      <!-- Right side (MD only) -->
      <div ref="scrollCharsArea" class="w-3/5 text-center <md:hidden" style="padding-inline: 4vw">
        <div v-for="({ title, paragraph, animatedImage }, key) in scrollableChars.sections" :key ref="scrollCharsSections" class="h-screen">
          <base-picture v-bind="animatedImage" />
          <h2 v-html="title" class="mb-6 fw-medium lh-none" style="font-size: 2.25vw" />
          <p v-html="paragraph" class="lh-tight" style="font-size: 1vw" />
        </div>
      </div>
      <!-- Sticky navigation (MD only) -->
      <div class="sticky top-0 h-screen w-1/5 f-col items-end justify-center pr-6 <md:hidden">
        <div class="relative">
          <div class="absolute right-3 top-4 bg-grey-60" style="height:calc( 100% - 2rem ); width: 1px" />
          <div
            v-for="({ dotTitle }, key) in scrollableChars.sections"
            :key
            class="relative flex items-center justify-end space-x-3"
            :class="{ 'mb-10': key < scrollableChars.sections.length - 1 }"
          >
            <p class="truncate duration" :class="scrollCharsIndex === key ? 'op-100' : 'op-0'">
              {{ dotTitle }}
            </p>
            <base-button
              class="relative aspect-square w-6 rounded-full duration"
              :class="scrollCharsIndex === key ? 'scale-50 bg-grey-60' : 'bg-white'"
              @click="scrollToElement(scrollCharsSections[key], { offset: 35 })"
            >
              <div class="animation-pulsate absolute left-1/2 full rounded-full bg-white" />
            </base-button>
          </div>
        </div>
      </div>
    </section>
    <!-- Benefits section -->
    <section class="f-col">
      <div class="w-full bg-black text-center md:flex">
        <div v-for="({ title, paragraph, image, icon }, key) in benefits.benefits" :key class="group relative md:w-1/3">
          <base-picture class="w-full duration group-hover:op-50" v-bind="image" />
          <div class="absolute-0 full f-col center c-white duration group-hover:translate-y-0 group-hover:op-100 md:translate-y-3 md:op-0 space-y-3">
            <base-picture v-bind="icon" style="max-width: 6rem" />
            <h2 v-style:text-size="titleTextSize1" class="fw-medium">
              {{ title }}
            </h2>
            <p v-style:text-size="paragraphTextSize1" v-html="paragraph" class="w-2/3" />
          </div>
        </div>
      </div>
      <div
        class="animation-bg-scroll-x"
        :style="{
          filter: 'invert',
          aspectRatio: '12 / 1',
          background: `#dadada url(${benefits.bgStripeSrc}) repeat-x center / 20% auto`,
        }"
      />
    </section>
    <!-- Product grid -->
    <section
      class="bg-black text-center c-white"
      data-scroll-el="productGrid"
      style="padding: 3rem 8vw"
    >
      <h2 v-style:text-size="titleTextSize1" class="mb-6 fw-medium">
        {{ productGrid.title }}
      </h2>
      <p v-style:text-size="paragraphTextSize1" v-html="productGrid.paragraph" class="mx-a mb-6 md:w-3/5" />
      <base-tabs-root :key="activeTab" v-model="activeTab">
        <base-tabs-list v-if="productGrid.activities.length > 1">
          <base-scroll class="flex center">
            <vf-button
              v-for="({ title }, key) in productGrid.activities"
              :key
              class="mx-1 mb-6"
              size="sm"
              :value="key"
              :variant="activeTab === key ? 'primary-inverse' : 'primary'"
              @click="switchActivityTab(key)"
            >
              {{ title }}
            </vf-button>
          </base-scroll>
        </base-tabs-list>
        <base-tabs-content
          v-for="(groups, key) in productGrid.activities"
          :key
          :value="key"
        >
          <vf-carousel class="f-col">
            <template
              v-for="({ products, title, paragraph: groupParagraph }, groupKey) in groups.groups"
              :key="groupKey"
            >
              <div
                v-for="({ image, to }, prodKey) in products"
                :key="prodKey"
                class="w-full b b-white md:w-1/4"
              >
                <div class="group h-full f-col">
                  <div class="p-8 space-y-10">
                    <base-button @click="scrollToProductDetails(products[prodKey])">
                      <base-picture
                        class="relative duration group-hover:scale-105"
                        v-bind="image"
                      />
                    </base-button>
                    <vf-button class="w-full <md:hidden" size="tiny" :to variant="secondary-inverse">
                      {{ $t.shopNow }}
                    </vf-button>
                  </div>
                  <!-- Grouped for MD , ungrouped for SM -->
                  <div
                    v-style:w="{ md: `${100 * products.length}%` }"
                    class="relative h-full f-col b-t b-white bg-black p-5 md:p-8"
                    :class="{ 'md:hidden': prodKey }"
                  >
                    <h2 v-style:text-size="titleTextSize2" v-html="title" class="fw-medium uppercase" />
                    <p v-style:text-size="paragraphTextSize2" v-html="groupParagraph" />
                    <vf-button class="mt-5 md:hidden" size="tiny" :to variant="primary-inverse">
                      {{ $t.shopNow }}
                    </vf-button>
                  </div>
                </div>
              </div>
            </template>
            <template #prev="{ active, action }">
              <base-button
                :aria-label="$t.previous"
                class="absolute right-1/2 md:hidden"
                :disabled="!active"
                style="bottom:-4.1rem; margin-right:6rem; z-index:100"
                @click="action"
              >
                <vf-icon dir="left" name="chevron" size="lg" />
              </base-button>
            </template>
            <template #next="{ active, action }">
              <base-button
                :aria-label="$t.next"
                class="absolute left-1/2 md:hidden"
                :disabled="!active"
                style="bottom:-4.1rem; margin-left:6rem; z-index:100"
                @click="action"
              >
                <vf-icon dir="right" name="chevron" size="lg" />
              </base-button>
            </template>
            <template #pagination="{ active, pages }">
              <base-progress
                v-slot="{ percentage }"
                class="m-a mt-12 rounded-full bg-grey-20 md:hidden"
                :max="pages"
                role="presentation"
                style="width:12rem; height:0.25rem"
                :value="active + 1"
              >
                <div
                  class="h-full rounded-full bg-white"
                  :style="{
                    'transform': `scaleX(${percentage / 100})`,
                    'transform-origin': 'left',
                    'transition': 'transform .6s cubic-bezier(.16,1,.3,1)',
                  }"
                />
              </base-progress>
            </template>
          </vf-carousel>
        </base-tabs-content>
      </base-tabs-root>
    </section>
    <!-- Product details carousel -->
    <section class="bg-black c-white" data-scroll-el="productDetail">
      <base-scroll ref="scroll">
        <template
          v-for="({ products, title }, key) in productGrid.activities[activeTab].groups"
          :key
        >
          <div
            v-for="({ image, to, paragraph: longParagraph, dots, tables }, prodKey) in products"
            :key="prodKey"
            class="w-full f-col md:flex-row-reverse"
            style="padding: 3rem 8vw"
          >
            <!-- Right column -->
            <div class="relative md:w-1/2 md:py-16 md:pr-16">
              <div class="md:absolute md:right-0 md:top-0 md:w-2/5">
                <h2 v-style:text-size="{ sm: '5vw', md: '.9vw' }" class="mb-3 b-b pb-3 c-$theme uppercase" style="border-color: #488bcb">
                  {{ products[prodKey].dotTitle }}
                </h2>
                <p
                  v-style:text-size="{ sm: '4vw', md: '.9vw' }"
                  v-html="products[prodKey].activeDot?.paragraph || dots[0].paragraph"
                />
              </div>
              <div v-style=" { ml: { md: '-20%' }, my: { sm: '2rem' } }" class="relative w-full">
                <base-picture v-bind="image" />
                <base-button
                  v-for="({ left, top }, dotKey) in dots"
                  :key="dotKey"
                  class="absolute z-1 b b-white rounded-full p-2 duration"
                  :class="(
                    dots[dotKey] === products[prodKey].activeDot
                    || (!products[prodKey].activeDot && !dotKey)
                      ? 'bg-black c-white'
                      : 'bg-white c-black'
                  )"
                  :style="{ left, top }"
                  @click="products[prodKey].activeDot = dots[dotKey]"
                >
                  <vf-icon name="plus" size="sm" />
                </base-button>
              </div>
            </div>
            <!-- Left column -->
            <div class="relative md:w-1/2">
              <h2 v-style:text-size="{ sm: '12vw', md: '5.5vw' }" v-html="title" class="mb-3 ws-pre-wrap fw-medium lh-none uppercase" />
              <p v-style:text-size="{ sm: '4vw', md: '1.1vw' }" v-html="longParagraph" class="mb-6 md:w-2/3" />
              <vf-button class="mb-18" :to variant="tertiary-inverse">
                {{ $t.shopNow }}
              </vf-button>
              <div class="w-full md:absolute md:bottom-0 md:flex">
                <div
                  v-for="({ title: tableTitle, rows }, tableKey) in tables"
                  :key="tableKey"
                  class="<md:mb-6"
                  :class="{ 'md:mr-3 md:pr-3 md:b-r md:b-$theme': tables.length - 1 !== tableKey }"
                >
                  <div class="grid items-baseline lh-none" style="grid-template-columns: auto 1fr; height: fit-content">
                    <div v-style:text-size="{ sm: '4vw', md: '.75vw' }" class="col-span-2 mb-3 uppercase">
                      {{ tableTitle }}
                    </div>
                    <template v-for="({ value, title: rowTitle }, rowKey) in rows" :key="rowKey">
                      <div v-style:text-size="{ sm: '10vw', md: '2.5vw' }" class="items-end pr-3 c-$theme">
                        {{ value }}
                      </div>
                      <div v-style:text-size="{ sm: '4vw', md: '.75vw' }" class="c-$theme">
                        {{ rowTitle }}
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </base-scroll>
      <div class="w-ful flex bg-$theme c-black between">
        <base-button class="flex center duration" :class="{ 'op-0 pointer-events-none': !scroll?.activePage }" @click="scroll.prev()">
          <vf-icon dir="left" name="chevron" size="xl" />
          <p v-html="getAdjacentProductTitles().prev" class="uppercase" />
        </base-button>
        <base-button class="flex center duration" :class="{ 'op-0 pointer-events-none': scroll?.activePage === scroll?.pages - 1 }" @click="scroll.next()">
          <p v-html="getAdjacentProductTitles().next" class="uppercase" />
          <vf-icon name="chevron" size="xl" />
        </base-button>
      </div>
    </section>
  </main>
</template>

<script lang="ts" setup>
definePageMeta({ header: { transparent: true } })

const activeTab = ref(0)
const route = useRoute()
const router = useRouter()
const { $t } = useNuxtApp()
const { data, error } = await useApi().internal.getStaticPage('innovation/technologies/vectiv')
if (!data.value) throw createError({ ...error.value!, fatal: true })

const titleTextSize1 = { sm: 'max(8vw, 2rem)', md: 'clamp(1.25rem, 2.5vw, 3rem)' }
const paragraphTextSize1 = { sm: 'max(1rem, 3vw)', md: 'clamp(.9rem, 1.25vw, 1.5rem)' }
const titleTextSize2 = { sm: 'max(5vw, 1.3rem)', md: 'clamp(1rem, 1.3vw, 1.6rem)' }
const paragraphTextSize2 = { sm: 'max(.9rem, 4vw)', md: 'clamp(0.9rem, 1vw, 1rem)' }
const scroll = ref()

const { hero, benefits, productGrid, innovation, scrollableChars } = data.value

const innovationRef = ref<HTMLElement>()
const parallaxRef = ref<HTMLElement>()
const scrollCharsArea = ref<HTMLElement>()
const scrollCharsSections = ref<HTMLElement[]>([])
const { height: innovationElementHeight } = useElementBounding(innovationRef)
const { height: parallaxElementHeight } = useElementBounding(parallaxRef)

const scrollCharsIndex = computed(() =>
  scrollCharsArea.value && window
    ? Math.max(
        0,
        Math.min(
          scrollableChars.sections.length - 1,
          -Math.round(useElementBounding(scrollCharsArea.value).top.value / window.innerHeight
          )
        )
      )
    : 0
)

const parallaxHeight = computed(() => {
  if (typeof window !== 'undefined') {
    return window.innerWidth <= 768
      ? (parallaxElementHeight.value + innovationElementHeight.value)
      : (window.innerHeight + parallaxElementHeight.value)
  }
  return 0
})

const switchActivityTab = (key) => {
  activeTab.value = key
  scroll.value.scrollToIndex(0)
}

const getAdjacentProductTitles = () => {
  const productTitles = productGrid.activities[activeTab.value].groups.flatMap((group) =>
    group.products.map(() => group.title))
  const prodKey = scroll?.value?.activePage || 0
  return {
    prev: productTitles[!prodKey ? productTitles.length - 1 : prodKey - 1],
    next: productTitles[prodKey === productTitles.length - 1 ? 0 : prodKey + 1],
  }
}

const scrollToProductDetails = (prod) => {
  scrollToElement('productDetail', { offset: 35 })
  const products = productGrid.activities[activeTab.value].groups.flatMap(({ products }) => products)
  scroll.value.scrollToIndex(products.findIndex((product) => product === prod))
}

onMounted(() => {
  if (route.query.activity) {
    activeTab.value = productGrid.activities.findIndex((each) => each.query === route.query.activity)
    nextTick(() => {
      scrollToElement('productGrid', { offset: 35 })
    })
  }
  watch(activeTab, () => {
    router.push({ query: { activity: productGrid.activities[activeTab.value].query } })
  })
})
</script>
